/**
 * Client-side analytics environment gate.
 *
 * The PostHog project key is committed in `apps/frontend/.env` on purpose
 * (NEXT_PUBLIC_* vars are inlined at build time), which means EVERY build
 * carries the production key — including Railway PR previews and local dev.
 * Server-side events self-label `environment` via `resolveRollbarEnvironment`,
 * but client events had no gate at all: on 2026-08-05 a QA check-in on
 * `frontend-onark-pr-283.up.railway.app` landed `guestCheckedIn` in the prod
 * PostHog project and put `posthog-gate-check-church-b` in the
 * first-guest-check-in cohort alongside real churches.
 *
 * The gate is by RUNTIME hostname, not build-time env: church guest pages are
 * served on arbitrary custom domains, so an allowlist of production hosts is
 * impossible — but no real visitor ever browses a `*.railway.app` host or
 * localhost. Both posthog-js init paths (apps/frontend/utils/posthog.ts and
 * marketing/components/analytics.tsx) consult this before initializing.
 */

/** Railway-provided service domains — previews AND the prod service's own
 *  generated domain. Real traffic reaches prod via onark.app / church custom
 *  domains, so losing capture on the raw Railway host costs nothing. */
const RAILWAY_HOST_SUFFIX = '.railway.app';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

/**
 * True when `hostname` is a host whose analytics must never reach the prod
 * PostHog project: localhost / loopback (local dev, /verify runs) and every
 * Railway-generated domain (PR previews, preview-base, the raw prod service
 * domain).
 *
 * Set `NEXT_PUBLIC_POSTHOG_CAPTURE_NONPROD=true` (build-time, e.g. in a
 * gitignored `.env.local`) to deliberately re-enable capture for local
 * analytics debugging — the override is read by the callers, not here, so this
 * stays a pure predicate.
 */
export function isNonProductionAnalyticsHost(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase();

  if (LOCAL_HOSTNAMES.has(normalized)) return true;

  return (
    normalized.endsWith(RAILWAY_HOST_SUFFIX) ||
    normalized.endsWith('.localhost')
  );
}
